import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { formatMoney } from '../utils/money';

export default function ConsolidatedReportPanel({ modoOscuro, desde, hasta }) {
  const { secureFetch } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';

  const load = useCallback(async () => {
    if (!desde || !hasta) return;
    setLoading(true);
    setError('');
    try {
      const res = await secureFetch(`/api/reportes/consolidado?desde=${desde}&hasta=${hasta}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'No se pudo cargar el consolidado');
      setData(json);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [secureFetch, desde, hasta]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <section className={`rounded-2xl border p-4 ${box}`}>
        <p className={`text-xs ${textMuted}`}>Cargando consolidado multi-empresa…</p>
      </section>
    );
  }

  if (error) return null;
  if (!data?.negocios?.length) return null;

  return (
    <section className={`rounded-2xl border p-4 sm:p-5 ${box}`}>
      <div className="mb-3">
        <h2 className={`text-sm font-semibold ${textPri}`}>Consolidado multi-empresa</h2>
        <p className={`text-xs ${textMuted}`}>
          {data.desde} – {data.hasta} · comparación por negocio
        </p>
      </div>
      <div className={`mb-3 grid grid-cols-3 gap-2 rounded-xl border p-3 ${modoOscuro ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
        <div>
          <div className={`text-[10px] uppercase ${textMuted}`}>Ventas grupo</div>
          <div className={`text-sm font-semibold tabular-nums ${textPri}`}>{formatMoney(data.consolidado?.ventas)}</div>
        </div>
        <div>
          <div className={`text-[10px] uppercase ${textMuted}`}>Egresos grupo</div>
          <div className={`text-sm font-semibold tabular-nums ${textPri}`}>{formatMoney(data.consolidado?.egresos)}</div>
        </div>
        <div>
          <div className={`text-[10px] uppercase ${textMuted}`}>Flujo grupo</div>
          <div className={`text-sm font-semibold tabular-nums ${textPri}`}>{formatMoney(data.consolidado?.flujo)}</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className={textMuted}>
              <th className="text-left py-2 pr-2 font-semibold">Negocio</th>
              <th className="text-right py-2 px-2 font-semibold">Ventas</th>
              <th className="text-right py-2 px-2 font-semibold">Egresos</th>
              <th className="text-right py-2 pl-2 font-semibold">Flujo</th>
            </tr>
          </thead>
          <tbody>
            {data.negocios.map((row) => (
              <tr key={row.companyId || row.name} className={modoOscuro ? 'border-t border-gray-800' : 'border-t border-gray-100'}>
                <td className={`py-2 pr-2 font-medium ${textPri}`}>{row.name}</td>
                <td className="py-2 px-2 text-right tabular-nums">{formatMoney(row.ventas)}</td>
                <td className="py-2 px-2 text-right tabular-nums">{formatMoney(row.egresos)}</td>
                <td className={`py-2 pl-2 text-right tabular-nums font-semibold ${row.flujo >= 0 ? (modoOscuro ? 'text-green-300' : 'text-green-700') : (modoOscuro ? 'text-red-300' : 'text-red-700')}`}>
                  {formatMoney(row.flujo)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
